<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

interface DataTableInstance {
  loadData: () => void;
}

const { t, n } = useI18n();
const { initialized } = useFiltro();

const dataTableRef = ref<DataTableInstance | null>(null);
const dynamicColumns = ref<string[]>([]);
const temClassificacao = ref<boolean>(false);
const dataLoaded = ref<boolean>(false);

// NS 2026-0103: a classificação da categoria de despesa vem em duas chaves de texto da linha.
const CHAVES_CLASSIFICACAO = ['Natureza', 'Subcategoria'];
const rotulosNatureza = computed<Record<string, string>>(() => ({ FIXO: t('Fixa'), VARIAVEL: t('Variável') }));

const endpoint = computed(() => '/index.php/relatorios/resultado_financeiro/lista_resultado');

const tableColumns = computed(() => {
  // "Titulo" fica congelada: as colunas à direita são uma por franquia, então a
  // tabela cresce sem limite na horizontal. Rolar até uma franquia distante
  // deixava os valores sem o rótulo que diz o que cada linha é (chamado 2026-0016).
  const baseColumns = [{
    data: 'Titulo',
    title: '',
    width: 200,
    sortable: false,
    frozen: true
  }];

  const franchiseColumns = dynamicColumns.value.map(colName => ({
    data: colName,
    title: colName,
    width: 150,
    sortable: false,
    // `renderType` continua declarado porque é o que a EXPORTAÇÃO lê para sair
    // como número (exportRows devolve parseFloat, não texto formatado).
    renderType: 'currency',
    // A formatação de TELA é por LINHA, não por coluna. Cada linha traz o
    // próprio `Formatacao`: "unidade" para "Qtd Compromissos", "moeda" para o
    // resto. Com a coluna inteira em currency, a contagem de compromissos
    // aparecia como R$ 1.394,00 — dinheiro onde era quantidade
    // (chamado 2026-0014). O backend sempre mandou essa informação; a tela é
    // que a descartava.
    cellRenderer: ({ value, data }: { value: unknown; data: Record<string, unknown> }) => {
      const numero = parseFloat(String(value)) || 0;
      if (data?.Formatacao === 'unidade') {
        return n(numero, 'integer');
      }

      // Cada categoria de despesa mostra, ao lado do valor, a fatia que ela representa
      // do FATURAMENTO do mês (chamado 2026-0046). Já foi sobre o recebido (2026-0015),
      // mas franquia sem recebimento lançado ficava sem percentual nenhum. O backend
      // manda a razão (0.12) em `percent_faturamento_<mês>`; o formato 'percent' do i18n
      // é quem transforma em "12%". Linhas que não são despesa não têm a chave e
      // seguem só com o valor.
      const moeda = n(numero, 'currency');
      const razao = parseFloat(String(data?.[`percent_faturamento_${colName}`] ?? ''));
      return Number.isFinite(razao) && razao > 0 ? `${moeda} (${n(razao, 'percent')})` : moeda;
    }
  }));

  // Natureza e Subcategoria logo depois do título, só quando alguma categoria do
  // resultado está classificada — para quem não usa a classificação a tela segue igual.
  const classificacaoColumns = temClassificacao.value ? [
    {
      data: 'Natureza',
      title: t('Natureza'),
      width: 110,
      sortable: false,
      cellRenderer: ({ value }: { value: unknown }) => rotulosNatureza.value[String(value)] ?? ''
    },
    {
      data: 'Subcategoria',
      title: t('Subcategoria'),
      width: 140,
      sortable: false,
      cellRenderer: ({ value }: { value: unknown }) => (value == null ? '' : String(value))
    }
  ] : [];

  return [...baseColumns, ...classificacaoColumns, ...franchiseColumns];
});

const dataTableConfig = computed(() => ({
  tableId: 'tabelaResultadoFinanceiro',
  filename: `resultado_financeiro_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 100,
  pagination: false,
  rowSelection: 'single',
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  size: 'small',
  sortable: false,
  showFilters: false
}));

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
  refreshName: t('Atualizar'),
  printName: t('Imprimir'),
  debug: false,
  columns: {}
}));

const csvButtonConfig = computed(() => ({ show: true, text: t('CSV'), loading: false }));
const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));

const loadData = () => {
  const stack = new Error().stack;
  log('ResultadoFinanceiroTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (dataTableRef.value) dataTableRef.value.loadData();
};

const extractDynamicColumns = (data: unknown[]): string[] => {
  if (data && data.length > 0) {
    const firstRow = data[0] as Record<string, unknown>;
    // Chaves com "_" na frente são marcas internas do DataTable (`_processed`), não meses:
    // sem este corte a tela ganhava uma coluna "_processed" cheia de R$ 0,00 (chamado 2026-0046).
    const columns = Object.keys(firstRow).filter(
      key => key !== 'Titulo' && key !== 'Tipo' && key !== 'Formatacao' && !CHAVES_CLASSIFICACAO.includes(key) && !key.startsWith('_') && !key.startsWith('percent_') && !key.endsWith('_display')
    );
    log('ResultadoFinanceiroTable', 'extractDynamicColumns', 'Extracted dynamic columns', columns);
    return columns;
  }
  return [];
};

const handleDataLoaded = (data: unknown[]): void => {
  log('ResultadoFinanceiroTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  const newDynamicColumns = extractDynamicColumns(data);
  if (JSON.stringify(newDynamicColumns) !== JSON.stringify(dynamicColumns.value)) {
    log('ResultadoFinanceiroTable', 'handleDataLoaded', 'Updating dynamic columns');
    dynamicColumns.value = newDynamicColumns;
  }
  temClassificacao.value = (data as Record<string, unknown>[]).some(
    row => CHAVES_CLASSIFICACAO.some(chave => row?.[chave] != null && row[chave] !== '')
  );
  dataLoaded.value = true;
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`registros do resultado financeiro`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('ResultadoFinanceiroTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : error.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar resultado financeiro: ') + message);
};

// Named handler for proper cleanup
const onFilterUpdated = (): void => {
  log('ResultadoFinanceiroTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

onMounted(() => {
  log('ResultadoFinanceiroTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('ResultadoFinanceiroTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('ResultadoFinanceiroTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
});

defineExpose({ loadData });
</script>
